import {
  Button,
  IconButton,
  HStack,
  Stack,
  Text,
  Spinner,
  VStack,
  Divider,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { FiEdit2, FiExternalLink, FiCheck } from "react-icons/fi";

import actions from "../../../actions";
import {
  KYDAdminEventSummary,
  KYDEventBundle,
} from "@common/interfaces/KYDEvent";
import { useParams } from "react-router-dom";

import "react-datepicker/dist/react-datepicker.css";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useLoading } from "../../hooks/form";
import AddEditBundleDialog, {
  AddEditBundleDialogContext,
  withAddEditBundleDialog,
} from "../AddBundleDialog";
import { MarketingTable } from "../../EventDetail/Nav/Marketing/MarketingTable";
import BundleItems from "./BundleItems";
import { InstantSearch } from "react-instantsearch-core";
import { algoliasearch } from "algoliasearch";
import { useDialogWithOptions } from "../../../Common/Dialog";
import { Pagination } from "react-instantsearch";

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

const BundleDetail = () => {
  const [bundle, setBundle] = useState<KYDEventBundle | null>(null);
  const [isEditingEvents, setIsEditingEvents] = useState(false);
  const [eventsInBundle, setEventsInBundle] = useState<KYDAdminEventSummary[]>(
    []
  );
  const { bundle_id } = useParams();
  const [indexName, setIndexName] = useState<string>("");

  const [loading, updateLoading] = useLoading({
    tickettype: false,
    promocode: false,
    create_affiliates: false,
    blocking: false,
    event: false,
    events: false,
    metrics: false,
    bundle: false,
    event_order: false,
  });
  const bundleEventsOrderRef = useRef<KYDAdminEventSummary[]>([]);
  const [searchClient, setSearchClient] = useState<any>(null);
  const dialog = useDialogWithOptions();
  const editNameDialog = useDialogWithOptions(AddEditBundleDialogContext);
  const toast = useToast();
  const fetchBundle = useCallback(
    async (bundleId?: string, options: any = {}) => {
      if (!options.silent) {
        updateLoading("init", true);
      }
      let _;
      try {
        if (!bundleId) {
          throw Error("No bundle ID provided");
        }
        _ = await actions.ADMIN.fetchEventBundle(bundleId);
        setBundle(_);
      } catch (err) {
        dialog.open({ text: err.message });
      }
      if (!options.silent) {
        updateLoading("init", false);
      }
      return _;
    },
    [updateLoading]
  );

  const onUpdateBundle = useCallback(
    async ({ events, title }: { events?: string[]; title?: string }) => {
      updateLoading("bundle", true);
      try {
        await actions.ADMIN.updateEventBundle({
          bundle_id,
          data: {
            title: title || bundle.title,
            events: events || eventsInBundle.map((e) => e.id),
          },
        });
        toast({
          status: "success",
          title: "Bundle updated!",
          description: "Your bundle was updated",
        });
        setIsEditingEvents(false);
        await fetchBundle(bundle_id, {
          silent: true,
        });
      } catch (e) {
        dialog.open({
          text: e.message,
        });
      }
      updateLoading("bundle", false);
    },
    [bundle_id, updateLoading, eventsInBundle, bundle]
  );

  useEffect(() => {
    //@ts-ignore
    bundleEventsOrderRef.current = eventsInBundle;
  }, [eventsInBundle]);

  const onUpdateOrder = async () => {
    const newOrder = bundleEventsOrderRef.current;
    setEventsInBundle(newOrder);

    if (!isEditingEvents) {
      updateLoading("event_order", true);
      await onUpdateBundle({
        events: newOrder.map((e) => e.id),
      });
      updateLoading("event_order", false);
    }
  };

  const fetchAlgoliaConfig = useCallback(async () => {
    try {
      const response = await actions.ADMIN.fetchAlgoliaConfig();
      if (response.events_config) {
        const search = algoliasearch(
          response.events_config.app_id,
          response.events_config.api_key
        );
        search.clearCache();
        setSearchClient(search);
        setIndexName(response.events_config.index_name);
      }
    } catch (err) {
      dialog.open({ text: err.message });
    }
    updateLoading("events", false);
  }, []);

  useEffect(() => {
    if (bundle) {
      setEventsInBundle(bundle.events);
    }
  }, [bundle]);

  useEffect(() => {
    if (bundle_id) {
      fetchBundle(bundle_id);
    }
  }, [bundle_id, fetchBundle]);

  useEffect(() => {
    fetchAlgoliaConfig();
  }, [fetchAlgoliaConfig]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <AddEditBundleDialog
        bundle={bundle}
        isLoading={loading.bundle}
        onSave={async (data) => {
          await onUpdateBundle({ title: data.title });
          editNameDialog.close();
        }}
        onClose={() => {
          editNameDialog.close();
        }}
      />

      <Stack
        height="100%"
        spacing={0}
        direction={["column", "row"]}
        width={"full"}
      >
        <Stack
          spacing={{ base: "8", lg: "6" }}
          transform="translate3d(0,0,0)"
          overflowY="scroll"
          width="100%"
          height="100%"
          p={8}
        >
          {loading["init"] && (
            <VStack>
              <Spinner />
              <Text>Loading bundle...</Text>
            </VStack>
          )}

          {bundle && (
            <Stack
              spacing={10}
              maxWidth={["100%", "5xl"]}
              minWidth={["100%", "initial"]}
              width="100%"
              margin="0 auto"
            >
              <Stack direction={["column", "row"]}>
                <Stack
                  w="100%"
                  direction={{ base: "column", md: "row" }}
                  spacing={3}
                >
                  <Stack w="100%" spacing={4}>
                    <Stack
                      w="100%"
                      justify={"space-between"}
                      direction={{ base: "column", md: "row" }}
                    >
                      <Stack spacing={0} justifyContent={"center"}>
                        <HStack>
                          <Text fontSize={"xl"} fontWeight="bold">
                            {bundle.title}
                          </Text>{" "}
                          <IconButton
                            isDisabled={loading["blocking"]}
                            onClick={() => editNameDialog.open()}
                            icon={<FiEdit2 fontSize="1.25rem" />}
                            variant="ghost"
                            aria-label="Edit member"
                          />
                        </HStack>

                        <Text fontSize={"sm"}>
                          Created at: {bundle.display_created_at}
                        </Text>
                      </Stack>
                      <Button
                        variant={"outline"}
                        onClick={() => window.open(bundle.link, "_blank")}
                        rightIcon={<FiExternalLink />}
                      >
                        Go To Bundle
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>

              <Divider />
              <Stack spacing={"70px"}>
                <Stack>
                  {searchClient ? (
                    <InstantSearch
                      indexName={indexName}
                      searchClient={searchClient}
                      initialUiState={{
                        [indexName]: {
                          menu: {
                            status: "OPEN",
                          },
                        },
                      }}
                    >
                      <BundleItems
                        onUpdateOrder={onUpdateOrder}
                        eventsInBundle={eventsInBundle}
                        setEventsInBundle={setEventsInBundle}
                        onUpdateBundle={onUpdateBundle}
                      />
                    </InstantSearch>
                  ) : null}
                </Stack>
                <Stack>
                  <Stack p={2} overflowX={"scroll"}>
                    <MarketingTable
                      data={{
                        slug_id: bundle.organization.slug_id,
                        id: bundle.id,
                      }}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
};

export default withAddEditBundleDialog(BundleDetail);
